import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { push } from 'connected-react-router'
import { isFulfilled, isRejected } from '@reduxjs/toolkit'

import ApprovalListItem from '../../components/Approval/ListItem'
import SelectedPostPopup, { SELECTED_POPUP_PAGE } from '../../components/SelectedPostPopup'

import './ApprovalList.scss'
import { generatePath } from 'react-router-dom'
import {
  approveCurrentRoundThunk,
  unApproveCurrentRoundThunk,
  requireApproverRevisionThunk,
  startNextApprovalRoundThunk
} from '../../store/managePost/thunks'
import { getPostsForApprovalThunk, getApprovedPostsThunk } from '../../store/project/thunks'
import { setPostToApproveAction } from '../../store/managePost/slice'
import { setPostsForApprovalAction } from '../../store/modal/approvalPost/slice'
import { useDispatch, useSelector } from '../../store'
import { hideGlobalPreloaderAction, showGlobalPreloaderAction } from '../../store/preloader/slice'

import './ApprovalList.scss'
import { AUTH_ROUTE } from '../App/routes'
import { TUser } from '../../models'
import { TApproveCurrentRoundPayload, TUnApproveCurrentRoundPayload } from '../../store/managePost/newTypes'
import { useToasts } from 'react-toast-notifications'
import { TOAST_MESSAGE } from '../../components/UI/ToastNotification/ToastNotification'
import { selectNotApprovedPostsWithoutRequireRevision, selectApprovedPosts } from '../../selectors/PostSelector'
import StateManager from 'react-select'
import Preloader, { PRELOADER_TYPE } from '../../components/Preloader'
import { setHeaderView, showApprovalListAction } from '../../store/application/slice'
import { HEADER_VIEW, APPROVAL_LIST_TYPE } from '../../store/application/types'
import { Roles } from '../../models/roles';
import Calendar from '../../components/Calendar'

interface AppProps { }

const App: FC<AppProps> = ({ children }) => {
  const dispatch = useDispatch()

  const { project, selectedPosts, isLoading, approvalListType, user} = useSelector(
    ({ project, managePost, approvalModal, auth, app }) => ({
      project: project.currentProject,
      selectedPosts: approvalModal.posts_for_approval,
      latestSchedule: managePost.latestSchedule,
      postSocials: managePost.postSocials,
      user: auth.user,
      isLoading: project.loaders.approval.loading,
      approvalListType: app.approvalListType
    })
  )
  const unapproverPost = useSelector(selectNotApprovedPostsWithoutRequireRevision)
  const approvedPosts = useSelector(selectApprovedPosts)
  const notApprovedPosts =  approvalListType === APPROVAL_LIST_TYPE.APPROVED?approvedPosts:unapproverPost

  const { addToast } = useToasts()

  useEffect(() => {
    dispatch(setPostToApproveAction({ id: 0 }))
    dispatch(setHeaderView(HEADER_VIEW.APPROVALS))
  }, [])

  useEffect(() => {
    const getPostsForApprovalList = async () => {
      await dispatch(getPostsForApprovalThunk())
    }
    const getApprovedPostList = async () => {
      await dispatch(getApprovedPostsThunk())
    }
    
    if (project.id &&  approvalListType === APPROVAL_LIST_TYPE.PENDING) getPostsForApprovalList()
    if (project.id &&  approvalListType === APPROVAL_LIST_TYPE.APPROVED) getApprovedPostList()

  }, [dispatch, project.id, approvalListType])

  const startNextApprovalRound = useCallback(
    (data: TApproveCurrentRoundPayload) => dispatch(startNextApprovalRoundThunk(data)),
    [dispatch, startNextApprovalRoundThunk]
  )

  const setCurrectPostToApproval = (post: any) => {
    dispatch(setPostToApproveAction(post))
  }

  const requireApproverRevision = useCallback(
    ({ postId, activeSocial }: any) => {
      dispatch(
        push(
          generatePath(approvalListType === APPROVAL_LIST_TYPE.APPROVED?AUTH_ROUTE.UNAPPROVE_ITEM:AUTH_ROUTE.APPROVAL_ITEM, {
            projectId: project?.id as number,
            social: activeSocial
          }) + `?postId=${postId}`
        )
      )
    },
    [dispatch, requireApproverRevisionThunk, approvalListType]
  )

  const approveCurrentRound = useCallback(
    (data: TApproveCurrentRoundPayload) => dispatch(approveCurrentRoundThunk(data)),
    [dispatch, approveCurrentRoundThunk]
  )

  const unApproveCurrentRound = useCallback(
    (data: TUnApproveCurrentRoundPayload) => dispatch(unApproveCurrentRoundThunk(data)),
    [dispatch, unApproveCurrentRoundThunk]
  )

  const setSelectedPosts = (posts: any[]) => {
    dispatch(setPostsForApprovalAction(posts))
  }

  const setOrRemoveSelectedPosts = (post: any) => {
    const isFindedEvent = selectedPosts.find((selectedPost) => selectedPost.id === post.id)

    if (!isFindedEvent) {
      setSelectedPosts([...selectedPosts, post])
    } else {
      setSelectedPosts(selectedPosts.filter((selectedPost) => selectedPost.id !== post.id))
    }
  }

  const approveSelectedPosts = useCallback(async () => {
    selectedPosts.forEach(async (selectedPost, index) => {
      if(approvalListType === APPROVAL_LIST_TYPE.PENDING){
        if (selectedPost.master_social_post_id) {
          const approveRoundResponse = await dispatch(
            approveCurrentRoundThunk({ post_social_id: selectedPost.master_social_post_id })
          )
          if (isFulfilled(approveRoundResponse)) {
            addToast(TOAST_MESSAGE.POST_APPROVED, { appearance: 'success' })
          } else if (isRejected(approveRoundResponse)) {
            addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
          }
        } else {
          for (let i = 0; i < (selectedPost as any).post_socials.length; i++) {
            const approveRoundResponse = await dispatch(
              approveCurrentRoundThunk({ post_social_id: +(selectedPost as any).post_socials[i].id })
            )
            if (isFulfilled(approveRoundResponse)) {
              addToast(TOAST_MESSAGE.POST_APPROVED, { appearance: 'success' })
            } else if (isRejected(approveRoundResponse)) {
              addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
            }
          }
        }
      }else{
        if (selectedPost.master_social_post_id) {
          const unapproveRoundResponse = await dispatch(
            unApproveCurrentRoundThunk({ approval_id: selectedPost.approval_id })
          )
          if (isFulfilled(unapproveRoundResponse)) {
            addToast(TOAST_MESSAGE.POST_UNAPPROVED, { appearance: 'success' })
          } else if (isRejected(unapproveRoundResponse)) {
            addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
          }
        } else {
          for (let i = 0; i < (selectedPost as any).post_socials.length; i++) {
            const unapproveRoundResponse = await dispatch(
              unApproveCurrentRoundThunk({ approval_id: user?.agency_role === Roles.EXTERNAL_APPROVER?+(selectedPost as any).post_socials[i].external_approval_id : +(selectedPost as any).post_socials[i].internal_approval_id })
            )
            if (isFulfilled(unapproveRoundResponse)) {
              addToast(TOAST_MESSAGE.POST_UNAPPROVED, { appearance: 'success' })
            } else if (isRejected(unapproveRoundResponse)) {
              addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
            }
          }
        }
      }
      if (index === selectedPosts.length - 1) setSelectedPosts([])
    })
  }, [dispatch, selectedPosts, approveCurrentRoundThunk, startNextApprovalRoundThunk])

  const approvalListItemProps = {
    requireApproverRevision,
    approveCurrentRound,
    unApproveCurrentRound,
    startNextApprovalRound,
    setCurrectPostToApproval,
    setSelectedPosts: setOrRemoveSelectedPosts,
    socials: project.socials
  }

  const manageMultiplePostsProps = {
    setSelectedPosts,
    selectedPostIds: selectedPosts.map((post) => post.id),
    approveSelectedPosts,
    page: SELECTED_POPUP_PAGE.APPROVAL,
    approvalListType:approvalListType
  }

  return (
    <>
      <div className='approvals'>
        <div className='approvals__items-wrapper'>
          {isLoading ?
            <Preloader type={PRELOADER_TYPE.LOADING_CONTENT} /> :
            <>

                {!!notApprovedPosts.length &&
                  notApprovedPosts.map((post) => {
                    const checked = selectedPosts.find((selectedPost) => selectedPost.id === post.id)
                    return (
                      <div
                        className='approvals__items-wrapper__item-wrapper'
                        key={`approval-item-${post.id}`}
                      >
                        <ApprovalListItem
                          {...approvalListItemProps}
                          latestSchedule={post.latest_schedule}
                          post={post}
                          postSocials={post.post_socials}
                          checked={!!checked}
                        />
                      </div>
                    )
                  })}
                {!notApprovedPosts.length && (
                  <div className='approvals__items-wrapper__empty-list'>
                    There are no posts to approve.
                  </div>
                )}
            
            </>
          }
        </div>
        <SelectedPostPopup {...manageMultiplePostsProps} />
      </div>
    </>
  )
}

export default App
